import { Popover, PopoverButton, PopoverContent } from "@deere/fuel-react";
import AppsIcon from "@mui/icons-material/Apps";
import { Box, Typography } from "@mui/material";
import React, { useContext } from "react";

import {
  APPLICATION_LINK_JDPOINT,
  APPLICATION_LINK_MY_LOCATOR_ENTRY,
  APPLICATION_LINK_MY_LOCATOR_INVENTORY,
  APPLICATION_LINK_OTHER_DEALER_INVENTORY,
} from "../../../../../../constant";
import { ConfigContext } from "../../../../../../context/ConfigContext";
import { DealerAccountContext } from "../../../../../../context/DealerAccountContext";
import {
  CANADA_DEALER_LOCATION,
  US_DEALER_LOCATION,
} from "../../../../../../utils/constants";
import ExternalLinkButton from "../ExternalLinkButton/ExternalLinkButton";

interface MyApplicationProps {
  isOpen: boolean;
  onClose: () => void;
  onToggle: () => void;
  theme: any;
  t: (key: string) => string;
  showNonGlobalFeatureLinks: boolean;
  externalLinksVisibility: Map<string, boolean>;
}

const MyApplication: React.FC<MyApplicationProps> = ({
  isOpen,
  onClose,
  onToggle,
  theme,
  t,
  showNonGlobalFeatureLinks,
  externalLinksVisibility,
}) => {
  const {
    OTHER_DEALER_INVENTORY_LINK,
    MY_LOCATOR_INVENTORY_LINK,
    MY_LOCATOR_ENTRY_LINK,
    EXPERT_CONNECT_LINK,
    JDPOINT_URL,
    CCMS_LINK,
    PARTS_ADVISOR_LINK,
    NMQ_LINK,
    PDG_LINK,
  } = useContext(ConfigContext);

  const { dealerAccount } = useContext(DealerAccountContext);
  const accountId = dealerAccount?.id as string;
  const base64AccountId = btoa(accountId);
  const dealerCountry = dealerAccount?.location?.country;
  const isPdgAvailableCountry =
    !!dealerCountry &&
    [US_DEALER_LOCATION, CANADA_DEALER_LOCATION].includes(dealerCountry);
  const applicationLinks = [
    {
      text: t("header.myApplication.otherDealerInventoryLink"),
      url: `${OTHER_DEALER_INVENTORY_LINK}?accountID=${base64AccountId}&isCallFromPITM=true`,
      visibilityKey: APPLICATION_LINK_OTHER_DEALER_INVENTORY,
    },
    {
      text: t("header.myApplication.myLocatorInventoryLink"),
      url: `${MY_LOCATOR_INVENTORY_LINK}?accountID=${base64AccountId}&isCallFromPITM=true`,
      visibilityKey: APPLICATION_LINK_MY_LOCATOR_INVENTORY,
    },
    {
      text: t("header.myApplication.myLocatorEntryLink"),
      url: `${MY_LOCATOR_ENTRY_LINK}?accountID=${base64AccountId}&isCallFromPITM=true`,
      visibilityKey: APPLICATION_LINK_MY_LOCATOR_ENTRY,
    },
    {
      text: t("header.myApplication.jdpoint"),
      url: `${JDPOINT_URL}/dns/loadMain.con?accountID=${base64AccountId}`,
      visibilityKey: APPLICATION_LINK_JDPOINT,
    },
  ];

  const trademarkedLinks = [
    { text: t("header.myApplication.expertConnect"), url: EXPERT_CONNECT_LINK },
    { text: t("header.myApplication.ccms"), url: CCMS_LINK },
    { text: t("header.myApplication.partsAdvisor"), url: PARTS_ADVISOR_LINK },
    { text: t("header.myApplication.nmq"), url: NMQ_LINK },
    ...(isPdgAvailableCountry
      ? [{ text: t("header.myApplication.pdg"), url: PDG_LINK }]
      : []),
  ];

  return (
    <Popover showClose={false} isOpen={isOpen} onClose={onClose}>
      <PopoverButton
        data-testid="my-application-button"
        aria-label="My Applications"
        variant="text"
        sx={{
          ":hover": {
            background: theme.tokens.FuelColorGray50,
          },
          ":active": {
            background: theme.tokens.FuelColorBackgroundActionSecondaryPressed,
          },
        }}
        onClick={onToggle}
      >
        <AppsIcon
          sx={{
            color: theme.tokens.FuelColorTextIcon,
          }}
          data-testid="my-application-icon"
        />
        <Typography
          sx={{
            fontWeight: 600,
            p: 2,
            height: "40px",
            fontSize: "14px",
            color: theme.tokens.FuelColorTextTertiary,
          }}
        >
          {t("header.myApplication")}
        </Typography>
      </PopoverButton>
      <PopoverContent
        PaperProps={{
          sx: {
            marginLeft: "-3.625rem",
          },
        }}
      >
        <Box minWidth="200px" sx={{ left: "800px" }}>
          {applicationLinks.map(
            ({ text, url, visibilityKey }) =>
              !showNonGlobalFeatureLinks &&
              externalLinksVisibility.get(visibilityKey) && (
                <ExternalLinkButton
                  key={text}
                  url={url}
                  text={text}
                  theme={theme}
                />
              )
          )}

          {trademarkedLinks.map(({ text, url }) => {
            const isTrademarked =
              text === "ExpertConnect" || text === "Parts ADVISOR";
            return (
              <ExternalLinkButton
                key={text}
                url={url}
                text={
                  isTrademarked ? (
                    <>
                      {text}
                      <sup style={{ fontSize: "0.6em", marginLeft: "2px" }}>
                        TM
                      </sup>
                    </>
                  ) : (
                    text
                  )
                }
                theme={theme}
              />
            );
          })}
        </Box>
      </PopoverContent>
    </Popover>
  );
};

export default MyApplication;
